/* eslint-disable @typescript-eslint/indent */
import React, { Dispatch, RefObject, SetStateAction, useContext, useEffect, useState } from 'react';
import { format, isToday } from 'date-fns';
import closeWithShadow from '@assets/images/close-with-shadow.svg';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { AddonItems, CartItem, CartItemDetails } from '@customTypes/cart-items';
import ButtonBlackRoundedCorners from '@components/common/ButtonBlackRoundedCorners';
import { UserAction } from '@customTypes/context';
import Spinner from '@components/common/SmartCalendar/Spinner';
import CloseButton from '@components/common/CloseButton/CloseButton';
import Currency from '@components/common/Currrency/Currency';
import User from '@customTypes/user';
import { CheckoutInfo } from '@customTypes/checkout-info';
import ImageFallback from '@components/common/Image/ImageFallback';
import HotelDetails from '@customTypes/hotel-details';
import actionTypes from '@context/actionTypes';
import { ProductDetails } from '@customTypes/product-details';
import { getDetailedDate, getMonthAndYear } from '@helpers/date';
import useSessionStorage from '@hooks/useSessionStorage';
import { removeAddOnItem, removeSingleCartItem } from '@helpers/cartFunctions';
import useCheckout from '@hooks/useCheckout';
import useCart from '@hooks/useCart';
import useHotSpot from '@hooks/useHotSpot';
import { getCdnImageUrl } from '@utils/cloudflare';
import { getProductDetails } from '@utils/services';
import Image from 'next/image';
import { useCookies } from 'react-cookie';
import { FAIR_PRICING_VARIANT, FAIR_PRICING_SERVICE_FEE_RATE } from '@constants/fairPricing';
import { calculateFairPricingFee, calculateHotSpotDiscount } from '@helpers/product';
import { HotSpotMin } from '@customTypes/hot-spot';
import showClosedForSeasonBanner from '@helpers/showClosedForSeasonBanner';
import { store } from '@context/store';
import SmartCalendar from '@components/common/SmartCalendar/SmartCalendar';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import Button from '@solar/common/Button/ButtonV2';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import calendarIcon from '../../assets/images/calendar-icon-new.svg';
import styles from './YourCart.module.css';

type Props = {
  cartData: CartItemDetails | undefined;
  cartId: string;
  hotelId: number | undefined;
  isYourCartExpanded: boolean;
  checkoutDetails: CheckoutInfo | undefined;
  hotelDetails: HotelDetails;
  confirmedDate: Date | undefined;
  showFairPricing: boolean;
  productData: ProductDetails | undefined;
  borderColor: string;
  urlDate: string | undefined;
  productsRef: RefObject<HTMLDivElement> | null;
  showDateWarning: boolean;
  setCartData: Dispatch<SetStateAction<CartItemDetails | undefined>>;
  setIsYourCartExpanded: Dispatch<SetStateAction<boolean>>;
  dispatch: React.Dispatch<UserAction>;
  setCheckoutDetails: Dispatch<SetStateAction<CheckoutInfo | undefined>>;
  setShowDateWarning: Dispatch<SetStateAction<boolean>>;
  setBorderColor: Dispatch<SetStateAction<string>>;
  setConfirmedDate: (date: Date | undefined) => void;
};

export default function YourCart({
  cartData,
  cartId,
  hotelId,
  isYourCartExpanded,
  checkoutDetails,
  hotelDetails,
  confirmedDate,
  showFairPricing,
  productData,
  borderColor,
  urlDate,
  productsRef,
  showDateWarning,
  setCartData,
  dispatch,
  setIsYourCartExpanded,
  setCheckoutDetails,
  setConfirmedDate,
  setShowDateWarning,
  setBorderColor,
}: Props) {
  const { getItem } = useSessionStorage();
  const [cartDate, setCartDate] = useState<string>();
  const [isSpinnerLoading, setIsSpinnerLoading] = useState<boolean>(false);
  const [cartHotelId, setCartHotelId] = useState<number>(0);
  const [hotelProducts, setHotelProducts] = useState<ProductDetails | undefined>();
  const [cookies] = useCookies();
  const { trackRemoveFromCart } = useCart(checkoutDetails, hotelProducts);
  const { navigateToCheckoutConfirmation } = useCheckout();
  const { getSubtotal } = useHotSpot();
  const { state } = useContext(store);
  const user: User = cookies.userInformation;
  const { searchDate, fixedDateSelectorExperiment } = state;
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  useEffect(() => {
    async function fetchHotelProductDetails() {
      if (checkoutDetails) {
        const hotelProductsDetails = await getProductDetails(hotelDetails.metadata.hotel_id);
        setHotelProducts(hotelProductsDetails);
      }
    }

    fetchHotelProductDetails();
  }, [checkoutDetails, hotelDetails]);

  const removeItem = async (removedItem: CartItem, productType: string | null) => {
    const updatedCartData = await removeSingleCartItem(
      cartId,
      removedItem,
      dispatch,
      setIsSpinnerLoading,
      productType,
      removedItem.hotel_id ?? hotelId,
      cartData,
      user,
      false,
    );

    trackRemoveFromCart(removedItem, cartId, hotelDetails);

    setCartData(updatedCartData);
  };

  const removeAddOn = async (addOn: AddonItems) => {
    const updatedCartData = await removeAddOnItem(
      addOn,
      setIsSpinnerLoading,
      dispatch,
      cartData,
      user,
    );
    setCartData(updatedCartData);
  };

  const handleOnBookNowClick = async () => {
    if (confirmedDate && isToday(confirmedDate)) {
      dispatch({
        type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
        payload: true,
      });
    } else {
      navigateToCheckoutConfirmation(dispatch, setCartData, checkoutDetails);
    }
  };

  useEffect(() => {
    if (getItem(sessionStorageKeys.CART_DATE)) {
      setCartDate(getItem(sessionStorageKeys.CART_DATE));
    } else if (confirmedDate && getItem(sessionStorageKeys.SMART_CALENDAR_DATE)) {
      setCartDate(getItem(sessionStorageKeys.SMART_CALENDAR_DATE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedDate, getDetailedDate]);

  useEffect(() => {
    if (
      cartData &&
      cartData?.items.length > 0 &&
      cartData?.items[0] &&
      cartData?.items[0].hotel_id
    ) {
      setCartHotelId(cartData?.items[0].hotel_id);
      setCartDate(getItem(sessionStorageKeys.CART_DATE));
    } else {
      if (confirmedDate) setCartDate(getDetailedDate(confirmedDate));
      setIsYourCartExpanded(false);
      setCheckoutDetails(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData]);

  const showCartDetails = cartData && cartData?.items.length > 0 && cartHotelId;
  const hotelName = checkoutDetails ? checkoutDetails.hotel_name : hotelDetails.name;
  const hotelImage = checkoutDetails
    ? checkoutDetails.hotel_image
    : hotelDetails && hotelDetails.image[0].picture.details.url;

  const calculatePrice = ({
    totalItemPrice,
    applyFairPricing,
    applyHotSpot,
    hotSpot,
  }: {
    totalItemPrice: number;
    applyFairPricing: boolean;
    applyHotSpot: boolean;
    hotSpot: HotSpotMin | undefined;
  }) => {
    const fairPricingFee = applyFairPricing ? calculateFairPricingFee(totalItemPrice) : 0;

    const hotSpotDiscount = calculateHotSpotDiscount({
      totalProductPrice: totalItemPrice,
      applyHotSpot,
      hotSpot,
    });

    return Math.round(totalItemPrice + fairPricingFee - hotSpotDiscount);
  };

  return (
    <div
      className="w-full pb-2 max-h-full overflow-y-scroll d:overflow-hidden d:border d:border-rp-gray-divider d:rounded-xl d:bg-white"
      id="your-cart-container"
    >
      {fixedDateSelectorExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME && (
        <div
          className="flex flex-col w-full border-rp-gray-divider pb-4 pt-6 border-b-0"
          id="smart-calendar-container"
        >
          <div className="flex flex-col w-full px-22px">
            {/* Adding border-red-600 text here so that it is included by purge css */}
            <div
              className={`border border-solid py-2 px-5 rounded-xl flex items-center text-base font-rp-pn-regular h-14 w-full d:text-sm shadow-smartCalendar ${borderColor}`}
            >
              <div className="pr-5 pt-2 mt-0 h-9 w-9">
                <Image src={calendarIcon} alt="calendar" />
              </div>

              <SmartCalendar
                setConfirmedDate={setConfirmedDate}
                confirmedDate={searchDate}
                productDetails={productData}
                hotelId={hotelId}
                setShowDateWarning={setShowDateWarning}
                urlDate={urlDate}
                setBorderColor={setBorderColor}
                setCartData={setCartData}
                productsRef={productsRef}
              />
            </div>

            {hotelDetails.closed_for_season &&
              showClosedForSeasonBanner(hotelDetails.reopen_date, searchDate) && (
                <div className="tracking-0.3 d:flex d:flex-col d:items-start d:justify-center">
                  <p className="text-rp-red text-13 font-rp-pn-semi-bold">CLOSED FOR THE SEASON</p>
                  {hotelDetails.reopen_date && (
                    <p className="text-sm text-rp-dark-gray-2">
                      {`Open For Booking: ${getMonthAndYear(hotelDetails.reopen_date, true)}`}
                    </p>
                  )}
                </div>
              )}
          </div>

          {/* HOT SPOT */}
          {!searchDate && hotelDetails.hot_spot ? (
            <div className="font-medium leading-14px mt-4 text-rp-hot-spot text-sm -tracking-0.28px">
              Save{' '}
              {hotelDetails.hot_spot.discount_type === 'cash' ? (
                <Currency
                  value={hotelDetails.hot_spot.discount}
                  isoCodeClasses="font-medium leading-14px text-rp-hot-spot text-sm -tracking-0.28px"
                  valueClasses="font-medium leading-14px text-rp-hot-spot text-sm -tracking-0.28px"
                />
              ) : (
                `${hotelDetails.hot_spot.discount}%`
              )}{' '}
              when you book between{' '}
              {format(new Date(hotelDetails.hot_spot.start_visit_date), 'MM/dd/yyyy')} -
              {format(new Date(hotelDetails.hot_spot.end_visit_date), 'MM/dd/yyyy')}
            </div>
          ) : null}

          {showDateWarning && <p className="text-red-600 pt-1">Please choose date</p>}
        </div>
      )}
      {/* Header with text and date */}
      <div className="flex pl-5 h-60px justify-start items-center text-15 font-rp-pn-semi-bold w-full shadow-lg rounded-xl z-100 bg-white fixed d:w-96 d:hidden">
        <button
          type="button"
          className="relative flex items-center mt-1.5"
          onClick={() => setIsYourCartExpanded(false)}
        >
          <Image src={closeWithShadow} alt="close-button" />
        </button>
        <p className="text-center w-11/12 -ml-6 text-15">Your Cart</p>
      </div>
      <div className="px-22px mt-16 d:mt-0">
        {hotelDetails && (
          <div
            className={`flex justify-between items-center ${
              fixedDateSelectorExperiment !== AMPLITUDE_EXPERIMENT_VARIANT_NAME ? 'pt-5' : ''
            } mt-px pb-6`}
          >
            <div className="flex flex-col items-start justify-center">
              <h2 className="text-17 pr-5 font-rp-pn-semi-bold text-rp-primary-black tracking-0.3">
                {hotelName}
              </h2>
              {cartDate && fixedDateSelectorExperiment !== AMPLITUDE_EXPERIMENT_VARIANT_NAME && (
                <p className="text-sm font-rp-pn-semi-bold text-rp-primary-black opacity-50">
                  {getDetailedDate(new Date(cartDate))}
                </p>
              )}
            </div>
            {fixedDateSelectorExperiment !== AMPLITUDE_EXPERIMENT_VARIANT_NAME && (
              <div className="min-w-102 h-74px">
                <ImageFallback
                  src={getCdnImageUrl(320, hotelImage, '', 100)}
                  blurDataUrl={getCdnImageUrl(32, hotelImage, '', 10)}
                  alt={hotelDetails.name}
                  className="rounded-xl"
                  height={74}
                  width={104}
                  loading="eager"
                />
              </div>
            )}
          </div>
        )}
        {isSpinnerLoading && <Spinner />}

        {/* Container for all the cart items */}
        {showCartDetails ? (
          <div className="text-15 overflow-scroll mb-30 d:overflow-hidden d:mb-2">
            {cartData?.items.map((item) => (
              <div key={item.id}>
                {/* One row each for individually billed items */}
                {item.individual &&
                  item.no_of_adults > 0 &&
                  Array.from(Array(item.no_of_adults).keys()).map((i) => (
                    <div
                      key={i + 1}
                      className="flex justify-between items-center h-auto py-3 border-t border-rp-gray-divider"
                    >
                      <p>
                        {`${item.name} - adult (`}
                        <span>
                          <Currency
                            value={calculatePrice({
                              totalItemPrice: item.adult_price,
                              applyFairPricing: showFairPricing,
                              applyHotSpot: item.is_hot_spot || false,
                              hotSpot: cartData.hot_spot,
                            })}
                            valueClasses="text-15"
                            isoCodeClasses="text-15"
                          />
                        </span>
                        )
                      </p>
                      <CloseButton onClick={() => removeItem(item, 'adult')} />
                    </div>
                  ))}

                {item.individual &&
                  item.no_of_childs > 0 &&
                  Array.from(Array(item.no_of_childs).keys()).map((i) => (
                    <div
                      key={i + 1}
                      className="flex justify-between border-t border-rp-gray-divider items-center h-auto py-3"
                    >
                      <p>
                        {`${item.name} - child (`}
                        <span>
                          <Currency
                            value={calculatePrice({
                              totalItemPrice: item.child_price,
                              applyFairPricing: showFairPricing,
                              applyHotSpot: item.is_hot_spot || false,
                              hotSpot: cartData.hot_spot,
                            })}
                            valueClasses="text-15"
                            isoCodeClasses="text-15"
                          />
                        </span>
                        )
                      </p>
                      <CloseButton onClick={() => removeItem(item, 'child')} />
                    </div>
                  ))}

                {/* Add-ons for individually billed items */}
                {item.individual &&
                  item.addon_items &&
                  item.addon_items.length > 0 &&
                  item.addon_items.map((addOn) => (
                    <div
                      key={addOn.id}
                      className="flex justify-between border-t border-rp-gray-divider items-center h-auto py-3"
                    >
                      <p>
                        {`${addOn.name} - (`}
                        <span>
                          <Currency
                            value={
                              showFairPricing
                                ? Math.round(+addOn.total + calculateFairPricingFee(addOn.total))
                                : +addOn.total
                            }
                            valueClasses="text-15"
                            isoCodeClasses="text-15"
                          />
                        </span>
                        <span>{`) for ${addOn.units}`}</span>
                      </p>
                      <CloseButton onClick={() => removeAddOn(addOn)} />
                    </div>
                  ))}

                {/* One row in total for non-individually billed items */}
                {!item.individual && item.no_of_adults > 0 && (
                  <div className="flex justify-between items-center h-auto py-3 border-t border-rp-gray-divider">
                    <p className="pr-2">
                      {`${item.name} - ${item.no_of_adults} ${
                        item.no_of_adults > 1 ? 'adults' : 'adult'
                      }${
                        item.no_of_childs > 0
                          ? `, ${item.no_of_childs} ${item.no_of_childs > 1 ? 'children' : 'child'}`
                          : ''
                      } ${'('}`}
                      <span>
                        <Currency
                          value={calculatePrice({
                            totalItemPrice: item.adult_price,
                            applyFairPricing: showFairPricing,
                            applyHotSpot: item.is_hot_spot || false,
                            hotSpot: cartData.hot_spot,
                          })}
                          valueClasses="text-15"
                          isoCodeClasses="text-15"
                        />
                      </span>
                      )
                    </p>

                    <CloseButton onClick={() => removeItem(item, null)} />
                  </div>
                )}

                {/* Add-ons for non-individually billed items */}
                {!item.individual &&
                  item.addon_items &&
                  item.addon_items.length > 0 &&
                  item.addon_items.map((addOn) => (
                    <div
                      key={addOn.id}
                      className="flex justify-between border-t border-rp-gray-divider items-center h-auto py-3"
                    >
                      {addOn.type !== 'single' && (
                        <p>
                          {`${addOn.name} - (`}
                          <span>
                            <Currency
                              value={
                                showFairPricing
                                  ? Math.round(+addOn.total + calculateFairPricingFee(addOn.total))
                                  : +addOn.total
                              }
                              valueClasses="text-15"
                              isoCodeClasses="text-15"
                            />
                          </span>
                          {`) for ${addOn.units}`}
                        </p>
                      )}
                      {addOn.type === 'single' && (
                        <p>
                          {`${addOn.name} - (`}
                          <span>
                            <Currency
                              value={
                                showFairPricing
                                  ? Math.round(+addOn.total + calculateFairPricingFee(addOn.total))
                                  : +addOn.total
                              }
                              valueClasses="text-15"
                              isoCodeClasses="text-15"
                            />
                          </span>
                          )
                        </p>
                      )}
                      <CloseButton onClick={() => removeAddOn(addOn)} />
                    </div>
                  ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="h-10 text-15 tracking-0.3 border-t border-rp-gray-divider pt-4 mb-4">
            <p className="opacity-50">Please select a product to continue</p>
          </div>
        )}
      </div>

      <div
        className={`fixed bg-white w-full px-22px pb-1.5 d:relative ${
          isYourCartExpanded ? styles.footerOpen : styles.footerClose
        }`}
      >
        <div
          className={`font-rp-pn-semi-bold text-17 w-full flex flex-wrap justify-between items-center border-t border-rp-gray-divider py-3 mb-0.5 d:w-auto ${
            showCartDetails ? 'd:-mt-2' : 'd:-mb-3'
          }`}
        >
          {showFairPricing && (
            <div className="flex-shrink-0 flex justify-between mb-2" style={{ flexBasis: '100%' }}>
              <p className="font-rp-pn-semi-bold text-17">Fees:</p>
              <p className="text-15 font-rp-pn-regular">
                {FAIR_PRICING_VARIANT === 'variant1' && 'Included in product price'}
                {FAIR_PRICING_VARIANT === 'variant2' && (
                  <Currency
                    value={
                      checkoutDetails
                        ? (checkoutDetails.subtotal * FAIR_PRICING_SERVICE_FEE_RATE).toString()
                        : '0'
                    }
                    valueClasses="text-17 font-rp-pn-semi-bold"
                    precision={0}
                  />
                )}
              </p>
            </div>
          )}

          <p>Subtotal: </p>
          <p>
            <Currency
              value={checkoutDetails ? getSubtotal(checkoutDetails, showFairPricing) : '0'}
              valueClasses="text-17 font-rp-pn-semi-bold"
              precision={0}
            />
          </p>
        </div>

        {/* Book now button */}
        {showCartDetails &&
          (!ctasUpdateExperiment || ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME ? (
            <div className="h-12 d:mb-2">
              <ButtonBlackRoundedCorners
                fixedDateSelectorExperiment={fixedDateSelectorExperiment}
                onClick={handleOnBookNowClick}
              >
                Book Now
              </ButtonBlackRoundedCorners>
            </div>
          ) : (
            <Button
              size="lg"
              onClick={handleOnBookNowClick}
              variant={
                ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                  ? 'primary'
                  : 'ultramarine'
              }
            >
              Reserve
            </Button>
          ))}
      </div>
    </div>
  );
}
